/**
 * Centralised presentation metadata for every status union in MicaraHR.
 *
 * Requirement §9/§19: a status is never communicated by colour alone. Each
 * entry carries a label, an icon, a semantic tone and two explanations — one
 * written for the employee, one for the manager or HR reviewer.
 *
 * These catalogues are plain data with no React Native dependency, so the
 * future HR & Administration web app renders the exact same vocabulary.
 */
import type { ApprovalStatus, ApprovalStepStatus, ApprovalRequestType } from './approval';
import type {
  AttendanceCorrectionReason,
  AttendanceEventType,
  AttendanceRiskFlagCode,
  AttendanceSecurityDecision,
  AttendanceStatus,
  OfflineQueueItemStatus,
  OfflineSyncOutcome,
} from './attendance';
import type { SemanticTone, StatusCatalog, StatusMetadata } from './common';
import type { UserRole } from './employee';
import type { OvertimeType } from './overtime';
import type { TimesheetStatus } from './attendance';

/* -------------------------------------------------------------------------- */
/* Attendance statuses                                                         */
/* -------------------------------------------------------------------------- */

export const ATTENDANCE_STATUS_CATALOG: StatusCatalog<AttendanceStatus> = {
  not_started: {
    code: 'not_started',
    label: 'Not started',
    description: 'The shift has not been started yet.',
    icon: 'Clock',
    tone: 'neutral',
    employeeExplanation: 'You have not timed in for this shift yet.',
    managerExplanation: 'No time in has been recorded for this employee today.',
  },
  on_time: {
    code: 'on_time',
    label: 'On time',
    description: 'Time in was recorded within the grace period.',
    icon: 'CircleCheck',
    tone: 'success',
    employeeExplanation: 'You timed in within your shift grace period.',
    managerExplanation: 'Time in recorded within the shift grace period.',
  },
  late: {
    code: 'late',
    label: 'Late',
    description: 'Time in was recorded after the grace period.',
    icon: 'AlarmClock',
    tone: 'warning',
    employeeExplanation:
      'Your time in was after the grace period. If this is not correct, you can request a correction.',
    managerExplanation: 'Time in recorded after the shift grace period.',
  },
  working: {
    code: 'working',
    label: 'Working',
    description: 'The employee is currently timed in.',
    icon: 'Activity',
    tone: 'brand',
    employeeExplanation: 'You are timed in. Your working time is being counted.',
    managerExplanation: 'Currently timed in and working.',
  },
  on_break: {
    code: 'on_break',
    label: 'On break',
    description: 'A break is currently in progress.',
    icon: 'Coffee',
    tone: 'info',
    employeeExplanation: 'You are on break. Break time is not counted as working time.',
    managerExplanation: 'Currently on break.',
  },
  completed: {
    code: 'completed',
    label: 'Completed',
    description: 'Time in and time out were both recorded.',
    icon: 'CircleCheckBig',
    tone: 'success',
    employeeExplanation: 'Your attendance for this day is complete.',
    managerExplanation: 'Complete attendance record with time in and time out.',
  },
  incomplete: {
    code: 'incomplete',
    label: 'Incomplete',
    description: 'The attendance record is missing one or more events.',
    icon: 'CircleAlert',
    tone: 'warning',
    employeeExplanation:
      'This day is missing an attendance event. You can submit a correction request.',
    managerExplanation: 'Attendance record is missing one or more events.',
  },
  absent: {
    code: 'absent',
    label: 'Absent',
    description: 'No attendance was recorded on a scheduled workday.',
    icon: 'CalendarX',
    tone: 'danger',
    employeeExplanation:
      'No attendance was recorded for this scheduled workday. Submit a correction if this is wrong.',
    managerExplanation: 'No attendance recorded on a scheduled workday.',
  },
  rest_day: {
    code: 'rest_day',
    label: 'Rest day',
    description: 'A scheduled rest day.',
    icon: 'Sunset',
    tone: 'neutral',
    employeeExplanation: 'This is your scheduled rest day.',
    managerExplanation: 'Scheduled rest day for this employee.',
  },
  holiday: {
    code: 'holiday',
    label: 'Holiday',
    description: 'A company or public holiday.',
    icon: 'PartyPopper',
    tone: 'accent',
    employeeExplanation: 'This day is a holiday.',
    managerExplanation: 'Holiday. Premium treatment is handled by payroll.',
  },
  on_leave: {
    code: 'on_leave',
    label: 'On leave',
    description: 'An approved leave covers this day.',
    icon: 'Palmtree',
    tone: 'info',
    employeeExplanation: 'You are on approved leave for this day.',
    managerExplanation: 'Employee is on approved leave.',
  },
  missing_time_in: {
    code: 'missing_time_in',
    label: 'Missing time in',
    description: 'A time out exists without a matching time in.',
    icon: 'LogIn',
    tone: 'warning',
    employeeExplanation:
      'Your time in was not recorded for this day. Request a correction so payroll can be accurate.',
    managerExplanation: 'Time out recorded without a matching time in.',
  },
  missing_time_out: {
    code: 'missing_time_out',
    label: 'Missing time out',
    description: 'A time in exists without a matching time out.',
    icon: 'LogOut',
    tone: 'warning',
    employeeExplanation:
      'Your time out was not recorded for this day. Request a correction so payroll can be accurate.',
    managerExplanation: 'Time in recorded without a matching time out.',
  },
  pending_review: {
    code: 'pending_review',
    label: 'Pending review',
    description: 'The record was submitted and is waiting for manual review.',
    icon: 'Hourglass',
    tone: 'info',
    employeeExplanation:
      'This attendance event was recorded and is waiting for review by your manager or HR.',
    managerExplanation: 'Submitted for manual review. Verification could not be completed.',
  },
  flagged: {
    code: 'flagged',
    label: 'Needs review',
    description: 'Verification raised one or more risk flags.',
    icon: 'Flag',
    tone: 'warning',
    employeeExplanation:
      'This attendance event needs manual review because it could not be fully verified.',
    managerExplanation: 'Verification raised risk flags. Review the evidence before deciding.',
  },
  corrected: {
    code: 'corrected',
    label: 'Corrected',
    description: 'An approved correction changed this record.',
    icon: 'PencilLine',
    tone: 'brand',
    employeeExplanation: 'This record was updated through an approved correction request.',
    managerExplanation: 'Record updated by an approved correction request.',
  },
  /*
   * The one outcome of HR's "Return for correction": the evidence is not
   * accepted and the employee files a correction. It is named as that
   * decision everywhere — status, review state, history — rather than
   * "Rejected" in one place, "Returned for correction" in another and
   * "Evidence not accepted" in a third.
   */
  rejected: {
    code: 'rejected',
    label: 'Returned for correction',
    description: 'HR returned this day for correction during review.',
    icon: 'CircleX',
    tone: 'danger',
    employeeExplanation:
      'HR returned this day for correction. Submit a correction request with the right details.',
    managerExplanation: 'Returned for correction during review.',
  },
};

/** Statuses that mean the employee is inside an active working session. */
export const ACTIVE_ATTENDANCE_STATUSES: AttendanceStatus[] = ['working', 'on_break'];

/** Statuses that mean no attendance is expected for the day. */
export const NON_WORKING_ATTENDANCE_STATUSES: AttendanceStatus[] = [
  'rest_day',
  'holiday',
  'on_leave',
];

/** Statuses that require someone to look at the record. */
export const ATTENTION_ATTENDANCE_STATUSES: AttendanceStatus[] = [
  'flagged',
  'pending_review',
  'incomplete',
  'missing_time_in',
  'missing_time_out',
  'absent',
  'rejected',
];

/* -------------------------------------------------------------------------- */
/* Attendance event types                                                      */
/* -------------------------------------------------------------------------- */

export const ATTENDANCE_EVENT_CATALOG: StatusCatalog<AttendanceEventType> = {
  time_in: {
    code: 'time_in',
    label: 'Time In',
    description: 'Start of the working day.',
    icon: 'LogIn',
    tone: 'success',
    employeeExplanation: 'You started your shift.',
    managerExplanation: 'Employee started the shift.',
  },
  break_start: {
    code: 'break_start',
    label: 'Break Start',
    description: 'Start of a break.',
    icon: 'Coffee',
    tone: 'info',
    employeeExplanation: 'You started a break.',
    managerExplanation: 'Break started.',
  },
  break_end: {
    code: 'break_end',
    label: 'Break End',
    description: 'End of a break.',
    icon: 'Play',
    tone: 'info',
    employeeExplanation: 'You returned from break.',
    managerExplanation: 'Break ended.',
  },
  time_out: {
    code: 'time_out',
    label: 'Time Out',
    description: 'End of the working day.',
    icon: 'LogOut',
    tone: 'brand',
    employeeExplanation: 'You ended your shift.',
    managerExplanation: 'Employee ended the shift.',
  },
  manual_time_in: {
    code: 'manual_time_in',
    label: 'Manual Time In',
    description: 'Time in entered on the employee’s behalf.',
    icon: 'PenLine',
    tone: 'warning',
    employeeExplanation: 'This time in was entered manually and needs review.',
    managerExplanation: 'Manually entered time in. Verify before approving.',
  },
  manual_time_out: {
    code: 'manual_time_out',
    label: 'Manual Time Out',
    description: 'Time out entered on the employee’s behalf.',
    icon: 'PenLine',
    tone: 'warning',
    employeeExplanation: 'This time out was entered manually and needs review.',
    managerExplanation: 'Manually entered time out. Verify before approving.',
  },
  offline_time_in: {
    code: 'offline_time_in',
    label: 'Offline Time In',
    description: 'Time in captured while the device had no connection.',
    icon: 'CloudOff',
    tone: 'info',
    employeeExplanation: 'This time in was saved offline and sent after reconnecting.',
    managerExplanation: 'Captured offline. Compare device time against server receipt time.',
  },
  offline_time_out: {
    code: 'offline_time_out',
    label: 'Offline Time Out',
    description: 'Time out captured while the device had no connection.',
    icon: 'CloudOff',
    tone: 'info',
    employeeExplanation: 'This time out was saved offline and sent after reconnecting.',
    managerExplanation: 'Captured offline. Compare device time against server receipt time.',
  },
  attendance_adjustment: {
    code: 'attendance_adjustment',
    label: 'Attendance Adjustment',
    description: 'An approved correction changed this record.',
    icon: 'PencilLine',
    tone: 'brand',
    employeeExplanation: 'An approved correction adjusted your attendance.',
    managerExplanation: 'Adjustment applied from an approved correction request.',
  },
  attendance_cancellation: {
    code: 'attendance_cancellation',
    label: 'Attendance Cancellation',
    description: 'An attendance event was cancelled.',
    icon: 'Ban',
    tone: 'danger',
    employeeExplanation: 'This attendance event was cancelled.',
    managerExplanation: 'Attendance event cancelled.',
  },
};

/* -------------------------------------------------------------------------- */
/* Attendance security                                                         */
/* -------------------------------------------------------------------------- */

export const SECURITY_DECISION_CATALOG: StatusCatalog<AttendanceSecurityDecision> = {
  verified: {
    code: 'verified',
    label: 'Verified',
    description: 'All required checks passed.',
    icon: 'ShieldCheck',
    tone: 'success',
    employeeExplanation: 'Your attendance event passed all the checks your company requires.',
    managerExplanation: 'All configured verification checks passed.',
  },
  verified_with_advisory: {
    code: 'verified_with_advisory',
    label: 'Verified with advisory',
    description: 'Accepted, with minor advisories recorded.',
    icon: 'ShieldAlert',
    tone: 'info',
    employeeExplanation:
      'Your attendance was accepted. We noted a small issue for your reference — no action is needed.',
    managerExplanation: 'Accepted with advisory notes. No manual review required.',
  },
  pending_manual_review: {
    code: 'pending_manual_review',
    label: 'Pending manual review',
    description: 'Recorded, but a person needs to confirm it.',
    icon: 'Hourglass',
    tone: 'warning',
    employeeExplanation:
      'This attendance event needs manual review because it could not be fully verified. It has been recorded and your manager or HR will confirm it.',
    managerExplanation: 'Recorded but unverified. Review the evidence and decide.',
  },
  rejected: {
    code: 'rejected',
    label: 'Not accepted',
    description: 'The event was not accepted.',
    icon: 'CircleX',
    tone: 'danger',
    employeeExplanation:
      'This attendance event was not accepted. Please try again, or submit a correction request.',
    managerExplanation: 'Rejected by policy. No attendance was recorded.',
  },
  security_check_failed: {
    code: 'security_check_failed',
    label: 'Check could not run',
    description: 'A required verification step could not complete.',
    icon: 'ShieldOff',
    tone: 'danger',
    employeeExplanation:
      'A required check could not be completed on this device. Try again, or ask HR for help.',
    managerExplanation: 'A required verification step failed to execute.',
  },
};

export interface RiskFlagMetadata extends StatusMetadata<AttendanceRiskFlagCode> {
  severity: 'advisory' | 'review' | 'blocking';
  /** Points added to the risk score when this flag is raised. */
  weight: number;
  /** What the employee can do about it. */
  resolution: string;
}

export const RISK_FLAG_CATALOG: Record<AttendanceRiskFlagCode, RiskFlagMetadata> = {
  outside_geofence: {
    code: 'outside_geofence',
    label: 'Outside work location',
    description: 'The captured location is outside the approved geofence.',
    icon: 'MapPinOff',
    tone: 'warning',
    severity: 'review',
    weight: 30,
    employeeExplanation:
      'This attendance event needs manual review because its location is outside your approved work location.',
    managerExplanation: 'Captured location falls outside the configured geofence radius.',
    resolution: 'Add a short note explaining where you are working from today.',
  },
  poor_gps_accuracy: {
    code: 'poor_gps_accuracy',
    label: 'Weak GPS signal',
    description: 'The location accuracy is wider than the policy allows.',
    icon: 'Satellite',
    tone: 'warning',
    severity: 'review',
    weight: 15,
    employeeExplanation:
      'Your phone could not get a precise location. Moving near a window or outdoors usually helps.',
    managerExplanation: 'Reported GPS accuracy exceeds the configured maximum.',
    resolution: 'Move to an open area and refresh your location before submitting.',
  },
  location_permission_denied: {
    code: 'location_permission_denied',
    label: 'Location permission off',
    description: 'Location access was not granted for this attendance event.',
    icon: 'LocateOff',
    tone: 'warning',
    severity: 'review',
    weight: 25,
    employeeExplanation:
      'Location access is off, so we could not confirm where this attendance event happened.',
    managerExplanation: 'No location evidence: permission was not granted.',
    resolution: 'Turn on location for MicaraHR in your phone settings, then try again.',
  },
  mock_location_suspected: {
    code: 'mock_location_suspected',
    label: 'Location source unclear',
    description: 'The location may not have come from the device GPS.',
    icon: 'ShieldQuestionMark',
    tone: 'danger',
    severity: 'review',
    weight: 45,
    employeeExplanation:
      'We could not confirm that this location came from your phone’s GPS, so it needs manual review.',
    managerExplanation:
      'Mock-location signal detected. Prototype heuristic only — confirm with the employee.',
    resolution: 'Turn off any location-simulation apps and record the event again.',
  },
  unregistered_device: {
    code: 'unregistered_device',
    label: 'Device not registered',
    description: 'Attendance was attempted from an unregistered device.',
    icon: 'SmartphoneNfc',
    tone: 'warning',
    severity: 'review',
    weight: 35,
    employeeExplanation:
      'This phone is not yet registered for attendance. Register it, or ask HR to approve it.',
    managerExplanation: 'Device is not on the employee’s registered device list.',
    resolution: 'Register this device from Settings → Device Registration.',
  },
  device_integrity_concern: {
    code: 'device_integrity_concern',
    label: 'Device check concern',
    description: 'The device integrity check did not pass.',
    icon: 'ShieldAlert',
    tone: 'danger',
    severity: 'review',
    weight: 40,
    employeeExplanation:
      'Your phone did not pass the standard device check, so this event needs manual review.',
    managerExplanation:
      'Device integrity check reported a concern (rooted / jailbroken placeholder signal).',
    resolution: 'Use a company-approved device, or contact HR.',
  },
  duplicate_device: {
    code: 'duplicate_device',
    label: 'Device already in use',
    description: 'This device is registered to another employee.',
    icon: 'Copy',
    tone: 'warning',
    severity: 'review',
    weight: 30,
    employeeExplanation:
      'This phone is already registered to another employee, so this event needs review.',
    managerExplanation: 'Device fingerprint matches another employee’s registered device.',
    resolution: 'Use your own registered device, or ask HR to update the registration.',
  },
  multiple_accounts_on_device: {
    code: 'multiple_accounts_on_device',
    label: 'Multiple accounts on device',
    description: 'More than one employee account signed in on this device.',
    icon: 'Users',
    tone: 'warning',
    severity: 'review',
    weight: 25,
    employeeExplanation:
      'More than one employee account has used this phone recently, so this event needs review.',
    managerExplanation: 'Multiple employee accounts observed on the same device.',
    resolution: 'Sign out other accounts, or ask HR to review the device.',
  },
  selfie_verification_failed: {
    code: 'selfie_verification_failed',
    label: 'Selfie not verified',
    description: 'The attendance selfie could not be verified.',
    icon: 'ScanFace',
    tone: 'warning',
    severity: 'review',
    weight: 30,
    employeeExplanation:
      'Your selfie could not be verified. You can retake it, or submit for review.',
    managerExplanation: 'No selfie was captured, or the upload did not complete.',
    resolution: 'Retake the selfie in better lighting, facing the camera directly.',
  },
  wifi_validation_failed: {
    code: 'wifi_validation_failed',
    label: 'Network not recognised',
    description: 'The device is not on an approved company network.',
    icon: 'WifiOff',
    tone: 'info',
    severity: 'advisory',
    weight: 10,
    employeeExplanation:
      'You are not on a company network. This is only a note — your attendance was still recorded.',
    managerExplanation: 'Device was not connected to an approved company network.',
    resolution: 'Connect to the office Wi-Fi if you are on site.',
  },
  impossible_travel: {
    code: 'impossible_travel',
    label: 'Unusual travel distance',
    description: 'The distance from the previous event is not plausible.',
    icon: 'PlaneTakeoff',
    tone: 'danger',
    severity: 'review',
    weight: 45,
    employeeExplanation:
      'This location is far from your previous attendance event in a short time, so it needs review.',
    managerExplanation:
      'Distance and elapsed time between consecutive events are not physically plausible.',
    resolution: 'Add a note explaining your travel, or submit a correction request.',
  },
  offline_delay: {
    code: 'offline_delay',
    // The timeline's name for the same fact: one phrase, not two.
    label: 'Sent after reconnecting',
    description: 'The event was recorded offline and reached the server after it reconnected.',
    icon: 'CloudOff',
    tone: 'info',
    severity: 'advisory',
    weight: 12,
    employeeExplanation:
      'This event was saved offline and reached MicaraHR later than usual. It was still recorded.',
    managerExplanation: 'Recorded offline and sent after reconnecting.',
    resolution: 'No action needed — connect to the internet sooner when possible.',
  },
  selfie_missing: {
    code: 'selfie_missing',
    label: 'Selfie missing',
    description: 'The event arrived without the selfie the policy asks for.',
    icon: 'Camera',
    tone: 'warning',
    severity: 'review',
    weight: 20,
    employeeExplanation: 'This entry was recorded without a selfie, so HR will look at it.',
    managerExplanation: 'No selfie was attached although the attendance policy requires one.',
    resolution: 'Take the selfie when the app asks for it next time.',
  },
  signature_missing: {
    code: 'signature_missing',
    label: 'Not signed by the device',
    description: 'A queued event arrived without the device signature.',
    icon: 'ShieldAlert',
    tone: 'info',
    severity: 'advisory',
    weight: 5,
    employeeExplanation:
      'This entry waited on your phone before it was sent and could not be signed. It was still recorded.',
    managerExplanation: 'Queued event without a device signature.',
    resolution: 'No action needed. Opening the app while online lets it collect its security key.',
  },
  signature_invalid: {
    code: 'signature_invalid',
    label: 'Could not be verified',
    description: 'The device signature does not match the event.',
    icon: 'ShieldAlert',
    tone: 'danger',
    severity: 'review',
    weight: 40,
    employeeExplanation: 'This entry could not be verified, so HR will look at it.',
    managerExplanation: 'Device signature does not match the event contents.',
    resolution: 'Nothing to do now. HR may ask you about this entry.',
  },
  duplicate_event: {
    code: 'duplicate_event',
    label: 'Already recorded',
    description: 'The same event was submitted more than once.',
    icon: 'Copy',
    tone: 'info',
    severity: 'advisory',
    weight: 5,
    employeeExplanation: 'This was already recorded, so the repeat was ignored.',
    managerExplanation: 'Duplicate submission of the same attendance event.',
    resolution: 'No action needed.',
  },
  invalid_sequence: {
    code: 'invalid_sequence',
    label: 'Out of order',
    description: 'The event does not follow the one before it.',
    icon: 'TriangleAlert',
    tone: 'warning',
    severity: 'review',
    weight: 20,
    employeeExplanation: 'This entry came out of the usual order, so HR will check the day.',
    managerExplanation: 'Attendance event out of sequence with the previous event.',
    resolution: 'If a time is missing, file a correction for that day.',
  },
  timestamp_difference: {
    code: 'timestamp_difference',
    label: 'Device clock difference',
    description: 'The device clock differs from server time.',
    icon: 'ClockAlert',
    tone: 'warning',
    severity: 'review',
    weight: 20,
    employeeExplanation:
      'Your phone clock is different from MicaraHR’s time. The official time is the one recorded by the server.',
    managerExplanation: 'Device timestamp differs materially from the server receipt time.',
    resolution: 'Set your phone to automatic date and time.',
  },
  duplicate_attendance_event: {
    code: 'duplicate_attendance_event',
    label: 'Possible duplicate',
    description: 'A very similar event already exists.',
    icon: 'CopyCheck',
    tone: 'warning',
    severity: 'review',
    weight: 25,
    employeeExplanation:
      'A similar attendance event was already recorded, so this one needs review before it counts.',
    managerExplanation: 'A near-identical event already exists for this employee and time.',
    resolution: 'Check your attendance history before submitting again.',
  },
};

/* -------------------------------------------------------------------------- */
/* Approvals                                                                   */
/* -------------------------------------------------------------------------- */

export const APPROVAL_STATUS_CATALOG: StatusCatalog<ApprovalStatus> = {
  draft: {
    code: 'draft',
    label: 'Draft',
    description: 'Saved but not yet submitted.',
    icon: 'FileText',
    tone: 'neutral',
    employeeExplanation: 'This request is saved as a draft. It has not been sent for approval yet.',
    managerExplanation: 'Draft — not yet visible for approval.',
  },
  submitted: {
    code: 'submitted',
    label: 'Submitted',
    description: 'Sent and waiting to be routed.',
    icon: 'Send',
    tone: 'info',
    employeeExplanation: 'Your request has been submitted.',
    managerExplanation: 'Submitted and awaiting routing.',
  },
  pending_manager: {
    code: 'pending_manager',
    label: 'Pending manager',
    description: 'Waiting for the manager’s decision.',
    icon: 'UserCheck',
    tone: 'warning',
    employeeExplanation: 'Your manager is reviewing this request.',
    managerExplanation: 'Waiting for your decision.',
  },
  pending_hr: {
    code: 'pending_hr',
    label: 'Pending HR',
    description: 'Waiting for HR’s decision.',
    icon: 'Building2',
    tone: 'warning',
    employeeExplanation: 'HR is reviewing this request.',
    managerExplanation: 'Approved at manager level. Waiting for HR.',
  },
  approved: {
    code: 'approved',
    label: 'Approved',
    description: 'The request was approved.',
    icon: 'CircleCheck',
    tone: 'success',
    employeeExplanation: 'Your request was approved.',
    managerExplanation: 'Approved.',
  },
  partially_approved: {
    code: 'partially_approved',
    label: 'Partially approved',
    description: 'Approved with changes to what was requested.',
    icon: 'CircleDashed',
    tone: 'info',
    employeeExplanation: 'Your request was approved with changes. Open it to see the details.',
    managerExplanation: 'Approved with adjustments to the requested values.',
  },
  rejected: {
    code: 'rejected',
    label: 'Rejected',
    description: 'The request was not approved.',
    icon: 'CircleX',
    tone: 'danger',
    employeeExplanation: 'Your request was not approved. The reason is shown in the timeline.',
    managerExplanation: 'Rejected.',
  },
  cancelled: {
    code: 'cancelled',
    label: 'Cancelled',
    description: 'Cancelled before a decision was made.',
    icon: 'Ban',
    tone: 'neutral',
    employeeExplanation: 'This request was cancelled.',
    managerExplanation: 'Cancelled by the employee.',
  },
  withdrawn: {
    code: 'withdrawn',
    label: 'Withdrawn',
    description: 'Withdrawn by the employee after submission.',
    icon: 'Undo2',
    tone: 'neutral',
    employeeExplanation: 'You withdrew this request.',
    managerExplanation: 'Withdrawn by the employee.',
  },
  returned_for_changes: {
    code: 'returned_for_changes',
    label: 'Returned for changes',
    description: 'Sent back so the employee can update it.',
    icon: 'Reply',
    tone: 'warning',
    employeeExplanation:
      'This request was returned so you can update it. Open it, make the changes and submit again.',
    managerExplanation: 'Returned to the employee for changes.',
  },
  completed: {
    code: 'completed',
    label: 'Completed',
    description: 'Fully processed, nothing further is needed.',
    icon: 'CheckCheck',
    tone: 'success',
    employeeExplanation: 'This request is complete.',
    managerExplanation: 'Completed — no further action required.',
  },
};

export const APPROVAL_STEP_STATUS_CATALOG: StatusCatalog<ApprovalStepStatus> = {
  pending: {
    code: 'pending',
    label: 'Waiting',
    description: 'This step has not been decided yet.',
    icon: 'Clock',
    tone: 'neutral',
    employeeExplanation: 'Waiting for this approver.',
    managerExplanation: 'Awaiting decision.',
  },
  approved: {
    code: 'approved',
    label: 'Approved',
    description: 'This approver said yes.',
    icon: 'Check',
    tone: 'success',
    employeeExplanation: 'This approver approved your request.',
    managerExplanation: 'Approved at this step.',
  },
  rejected: {
    code: 'rejected',
    label: 'Rejected',
    description: 'This approver said no.',
    icon: 'X',
    tone: 'danger',
    employeeExplanation: 'This approver did not approve your request.',
    managerExplanation: 'Rejected at this step.',
  },
  returned: {
    code: 'returned',
    label: 'Returned',
    description: 'Returned for changes at this step.',
    icon: 'Reply',
    tone: 'warning',
    employeeExplanation: 'This approver asked you to make changes.',
    managerExplanation: 'Returned to the employee at this step.',
  },
  skipped: {
    code: 'skipped',
    label: 'Skipped',
    description: 'This step was not required.',
    icon: 'SkipForward',
    tone: 'neutral',
    employeeExplanation: 'This step was not needed for your request.',
    managerExplanation: 'Step skipped by policy.',
  },
  cancelled: {
    code: 'cancelled',
    label: 'Cancelled',
    description: 'The request was cancelled before this step.',
    icon: 'Ban',
    tone: 'neutral',
    employeeExplanation: 'This step was cancelled.',
    managerExplanation: 'Step cancelled.',
  },
};

export const APPROVAL_TYPE_LABELS: Record<
  ApprovalRequestType,
  { label: string; icon: string; tone: SemanticTone }
> = {
  leave: { label: 'Leave', icon: 'Palmtree', tone: 'info' },
  overtime: { label: 'Overtime', icon: 'Timer', tone: 'accent' },
  attendance_correction: { label: 'Attendance correction', icon: 'PencilLine', tone: 'warning' },
  schedule_change: { label: 'Schedule change', icon: 'CalendarSync', tone: 'brand' },
  document_acknowledgement: {
    label: 'Document acknowledgement',
    icon: 'FileCheck',
    tone: 'neutral',
  },
  profile_update: { label: 'Profile update', icon: 'UserPen', tone: 'neutral' },
};

/* -------------------------------------------------------------------------- */
/* Corrections, offline, timesheets, overtime                                  */
/* -------------------------------------------------------------------------- */

export const CORRECTION_REASON_CATALOG: StatusCatalog<AttendanceCorrectionReason> = {
  missing_time_in: {
    code: 'missing_time_in',
    label: 'Missing time in',
    description: 'No time in was recorded.',
    icon: 'LogIn',
    tone: 'warning',
    employeeExplanation: 'Your time in was not recorded at all.',
    managerExplanation: 'Employee reports a missing time in.',
  },
  missing_time_out: {
    code: 'missing_time_out',
    label: 'Missing time out',
    description: 'No time out was recorded.',
    icon: 'LogOut',
    tone: 'warning',
    employeeExplanation: 'Your time out was not recorded at all.',
    managerExplanation: 'Employee reports a missing time out.',
  },
  incorrect_time_in: {
    code: 'incorrect_time_in',
    label: 'Incorrect time in',
    description: 'The recorded time in is wrong.',
    icon: 'ClockAlert',
    tone: 'warning',
    employeeExplanation: 'The time in that was recorded is not correct.',
    managerExplanation: 'Employee disputes the recorded time in.',
  },
  incorrect_time_out: {
    code: 'incorrect_time_out',
    label: 'Incorrect time out',
    description: 'The recorded time out is wrong.',
    icon: 'ClockAlert',
    tone: 'warning',
    employeeExplanation: 'The time out that was recorded is not correct.',
    managerExplanation: 'Employee disputes the recorded time out.',
  },
  incorrect_break: {
    code: 'incorrect_break',
    label: 'Incorrect break',
    description: 'The recorded break duration is wrong.',
    icon: 'Coffee',
    tone: 'warning',
    employeeExplanation: 'The break time that was recorded is not correct.',
    managerExplanation: 'Employee disputes the recorded break duration.',
  },
  wrong_shift: {
    code: 'wrong_shift',
    label: 'Wrong shift',
    description: 'The wrong shift was assigned for the day.',
    icon: 'CalendarSync',
    tone: 'info',
    employeeExplanation: 'The shift assigned to you for this day is not the one you worked.',
    managerExplanation: 'Employee reports an incorrect shift assignment.',
  },
  work_from_home: {
    code: 'work_from_home',
    label: 'Work from home',
    description: 'The day was worked remotely.',
    icon: 'House',
    tone: 'info',
    employeeExplanation: 'You worked from home on this day.',
    managerExplanation: 'Employee states the day was worked remotely.',
  },
  field_work: {
    code: 'field_work',
    label: 'Field work',
    description: 'The day was worked outside an office location.',
    icon: 'MapPin',
    tone: 'info',
    employeeExplanation: 'You were doing field work on this day.',
    managerExplanation: 'Employee states the day was field work.',
  },
  device_issue: {
    code: 'device_issue',
    label: 'Device issue',
    description: 'The phone prevented recording attendance.',
    icon: 'SmartphoneCharging',
    tone: 'neutral',
    employeeExplanation: 'Your phone stopped you from recording attendance.',
    managerExplanation: 'Employee reports a device problem.',
  },
  location_issue: {
    code: 'location_issue',
    label: 'Location issue',
    description: 'Location could not be captured.',
    icon: 'MapPinOff',
    tone: 'neutral',
    employeeExplanation: 'Location could not be captured when you tried to record attendance.',
    managerExplanation: 'Employee reports a location capture problem.',
  },
  network_issue: {
    code: 'network_issue',
    label: 'Network issue',
    description: 'No connection was available.',
    icon: 'WifiOff',
    tone: 'neutral',
    employeeExplanation: 'You had no internet connection when you tried to record attendance.',
    managerExplanation: 'Employee reports a connectivity problem.',
  },
  other: {
    code: 'other',
    label: 'Other',
    description: 'A reason not covered by the list.',
    icon: 'CircleEllipsis',
    tone: 'neutral',
    employeeExplanation: 'Explain what happened in your own words.',
    managerExplanation: 'Free-text reason supplied by the employee.',
  },
};

export const OFFLINE_STATUS_CATALOG: StatusCatalog<OfflineQueueItemStatus> = {
  pending: {
    code: 'pending',
    label: 'Saved offline',
    description: 'Waiting for a connection to synchronise.',
    icon: 'CloudOff',
    tone: 'info',
    employeeExplanation:
      'Naka-save offline ang attendance event. Ipapadala ito kapag bumalik ang internet connection.',
    managerExplanation: 'Queued on the employee’s device; not yet received by the server.',
  },
  syncing: {
    code: 'syncing',
    label: 'Synchronising',
    description: 'Being sent to MicaraHR right now.',
    icon: 'RefreshCw',
    tone: 'brand',
    employeeExplanation: 'Sending this attendance event now.',
    managerExplanation: 'Synchronisation in progress.',
  },
  synced: {
    code: 'synced',
    label: 'Synchronised',
    description: 'Received and processed by MicaraHR.',
    icon: 'CloudCheck',
    tone: 'success',
    employeeExplanation: 'This attendance event has been received.',
    managerExplanation: 'Received and processed.',
  },
  failed: {
    code: 'failed',
    label: 'Not sent',
    description: 'Sending failed and can be retried.',
    icon: 'CloudAlert',
    tone: 'warning',
    employeeExplanation: 'We could not send this event yet. You can retry it.',
    managerExplanation: 'Synchronisation failed; retry available.',
  },
  rejected: {
    code: 'rejected',
    label: 'Not accepted',
    description: 'MicaraHR did not accept the event.',
    icon: 'CircleX',
    tone: 'danger',
    employeeExplanation: 'This event was not accepted. Submit a correction request instead.',
    managerExplanation: 'Rejected on synchronisation.',
  },
  conflict: {
    code: 'conflict',
    label: 'Conflict',
    description: 'The event conflicts with an existing record.',
    icon: 'GitMerge',
    tone: 'warning',
    employeeExplanation:
      'This event conflicts with attendance that is already recorded, so it needs review.',
    managerExplanation: 'Conflicts with an existing attendance record.',
  },
};

export const OFFLINE_OUTCOME_LABELS: Record<
  OfflineSyncOutcome,
  { label: string; tone: SemanticTone; message: string }
> = {
  accepted: {
    label: 'Accepted',
    tone: 'success',
    message: 'Your offline attendance event was accepted.',
  },
  accepted_with_review: {
    label: 'Accepted, needs review',
    tone: 'warning',
    message: 'Your offline attendance event was accepted and sent for manual review.',
  },
  rejected: {
    label: 'Not accepted',
    tone: 'danger',
    message: 'Your offline attendance event was not accepted. You can submit a correction request.',
  },
  conflict_detected: {
    label: 'Conflict detected',
    tone: 'warning',
    message: 'This event conflicts with attendance that was already recorded.',
  },
  duplicate_event: {
    label: 'Duplicate',
    tone: 'neutral',
    message: 'This attendance event had already been recorded.',
  },
  shift_already_closed: {
    label: 'Shift already closed',
    tone: 'warning',
    message: 'The shift was already closed when this event arrived, so it needs review.',
  },
};

export const TIMESHEET_STATUS_CATALOG: StatusCatalog<TimesheetStatus> = {
  open: {
    code: 'open',
    label: 'Open',
    description: 'The period is still collecting attendance.',
    icon: 'CalendarClock',
    tone: 'neutral',
    employeeExplanation: 'This period is still ongoing.',
    managerExplanation: 'Period still open for attendance.',
  },
  pending_submission: {
    code: 'pending_submission',
    label: 'For submission',
    description: 'Ready for the employee to submit.',
    icon: 'FileUp',
    tone: 'info',
    employeeExplanation: 'Your timesheet is ready to submit.',
    managerExplanation: 'Awaiting employee submission.',
  },
  submitted: {
    code: 'submitted',
    label: 'Submitted',
    description: 'Submitted and waiting to be routed.',
    icon: 'Send',
    tone: 'info',
    employeeExplanation: 'Your timesheet has been submitted.',
    managerExplanation: 'Submitted by the employee.',
  },
  pending_approval: {
    code: 'pending_approval',
    label: 'Pending approval',
    description: 'Waiting for the manager’s approval.',
    icon: 'UserCheck',
    tone: 'warning',
    employeeExplanation: 'Your manager is reviewing your timesheet.',
    managerExplanation: 'Waiting for your approval.',
  },
  approved: {
    code: 'approved',
    label: 'Approved',
    description: 'Approved and ready for payroll.',
    icon: 'CircleCheck',
    tone: 'success',
    employeeExplanation: 'Your timesheet was approved.',
    managerExplanation: 'Approved.',
  },
  locked: {
    code: 'locked',
    label: 'Locked',
    description: 'Locked ahead of payroll processing.',
    icon: 'Lock',
    tone: 'neutral',
    employeeExplanation: 'This period is locked. Corrections now go through HR.',
    managerExplanation: 'Locked for payroll. Changes require HR.',
  },
  payroll_processed: {
    code: 'payroll_processed',
    label: 'Payroll processed',
    description: 'Already used in a processed payroll run.',
    icon: 'ReceiptText',
    tone: 'success',
    employeeExplanation: 'This period has already been paid.',
    managerExplanation: 'Consumed by a processed payroll run.',
  },
};

export const OVERTIME_TYPE_CATALOG: StatusCatalog<OvertimeType> = {
  regular_day: {
    code: 'regular_day',
    label: 'Regular day',
    description: 'Overtime on an ordinary working day.',
    icon: 'Timer',
    tone: 'brand',
    employeeExplanation: 'Extra hours worked on a normal working day.',
    managerExplanation: 'Ordinary working day overtime.',
  },
  rest_day: {
    code: 'rest_day',
    label: 'Rest day',
    description: 'Work performed on a scheduled rest day.',
    icon: 'Sunset',
    tone: 'accent',
    employeeExplanation: 'Work you did on your scheduled rest day.',
    managerExplanation: 'Rest day work — check coverage before approving.',
  },
  holiday: {
    code: 'holiday',
    label: 'Holiday',
    description: 'Work performed on a holiday.',
    icon: 'PartyPopper',
    tone: 'accent',
    employeeExplanation: 'Work you did on a holiday.',
    managerExplanation: 'Holiday work. Premium is computed by payroll.',
  },
  night_work: {
    code: 'night_work',
    label: 'Night work',
    description: 'Work performed during night differential hours.',
    icon: 'Moon',
    tone: 'info',
    employeeExplanation: 'Work you did during night hours.',
    managerExplanation: 'Night differential hours claimed.',
  },
  emergency_work: {
    code: 'emergency_work',
    label: 'Emergency work',
    description: 'Unplanned work required by an urgent situation.',
    icon: 'TriangleAlert',
    tone: 'warning',
    employeeExplanation: 'Unplanned work you had to do for an urgent situation.',
    managerExplanation: 'Emergency work — supporting details expected.',
  },
};

/* -------------------------------------------------------------------------- */
/* Roles                                                                       */
/* -------------------------------------------------------------------------- */

export const ROLE_LABELS: Record<UserRole, string> = {
  super_administrator: 'Super Administrator',
  company_administrator: 'Company Administrator',
  hr_administrator: 'HR Administrator',
  hr_officer: 'HR Officer',
  payroll_administrator: 'Payroll Administrator',
  payroll_officer: 'Payroll Officer',
  recruiter: 'Recruiter',
  department_head: 'Department Head',
  manager: 'Manager',
  team_leader: 'Team Leader',
  employee: 'Employee',
  auditor: 'Auditor',
  asset_custodian: 'Asset Custodian',
  learning_administrator: 'Learning Administrator',
};

/* -------------------------------------------------------------------------- */
/* Lookup helpers                                                              */
/* -------------------------------------------------------------------------- */

export function attendanceStatusMeta(status: AttendanceStatus): StatusMetadata<AttendanceStatus> {
  return ATTENDANCE_STATUS_CATALOG[status] ?? ATTENDANCE_STATUS_CATALOG.not_started;
}

export function attendanceEventMeta(
  type: AttendanceEventType,
): StatusMetadata<AttendanceEventType> {
  return ATTENDANCE_EVENT_CATALOG[type] ?? ATTENDANCE_EVENT_CATALOG.time_in;
}

export function approvalStatusMeta(status: ApprovalStatus): StatusMetadata<ApprovalStatus> {
  return APPROVAL_STATUS_CATALOG[status] ?? APPROVAL_STATUS_CATALOG.draft;
}

export function securityDecisionMeta(
  decision: AttendanceSecurityDecision,
): StatusMetadata<AttendanceSecurityDecision> {
  return SECURITY_DECISION_CATALOG[decision] ?? SECURITY_DECISION_CATALOG.verified;
}

/** A code this build does not know yet: named neutrally, never as some other check. */
const UNKNOWN_RISK_FLAG: RiskFlagMetadata = {
  code: 'duplicate_event',
  label: 'Needs a closer look',
  description: 'A check the app does not have a description for.',
  icon: 'Info',
  tone: 'info',
  severity: 'advisory',
  weight: 0,
  employeeExplanation: 'HR may look at this entry.',
  managerExplanation: 'Unrecognised check.',
  resolution: 'No action needed.',
};

export function riskFlagMeta(code: AttendanceRiskFlagCode): RiskFlagMetadata {
  return RISK_FLAG_CATALOG[code] ?? UNKNOWN_RISK_FLAG;
}

export function correctionReasonMeta(
  reason: AttendanceCorrectionReason,
): StatusMetadata<AttendanceCorrectionReason> {
  return CORRECTION_REASON_CATALOG[reason] ?? CORRECTION_REASON_CATALOG.other;
}
